import { Suspense, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { paths } from 'src/config/paths'
import { VerificationBadge } from 'src/shared/components/icons/verification-badge'
import { Button } from 'src/shared/components/ui/buttons/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'src/shared/components/ui/popups/dialog'

import { useUserById } from '../../api/accounts/use-user-by-id'
import { useUserByUsername } from '../../api/accounts/use-user-by-username'
import { usePublicLicenses } from '../../api/licenses/use-public-licenses'
import { useUserReviews } from '../../api/marketplace/use-user-reviews'
import { ExpertiseIcon } from '../../assets/general-info/expertise'
import { LanguagesIcon } from '../../assets/general-info/languages'
import { VerifiedLicensesIcon } from '../../assets/general-info/licenses'
import { WorksAtIcon } from '../../assets/general-info/works-at'
import type { Expertise } from '../../schemas/expertise'
import { expertiseMap } from '../../schemas/expertise'
import type { Language } from '../../schemas/language'
import { languageMap } from '../../schemas/language'
import { professionMap } from '../../schemas/profession'
import type { Profession } from '../../schemas/profession'
import { statesMap } from '../../schemas/states'
import type { State } from '../../schemas/states'
import { CoverPhotoFallback } from '../profile-photos/cover-photo'
import { ProfilePhotoFallback } from '../profile-photos/profile-avatar'

import GeneralInfoSection from './general-info-section'
import OtherInfoContainer from './other-info-container'
import PublicProfileUserCardSkeleton from './public-profile-user-card-skeleton'
import RequestCallButton from './ui/request-call-btn'

type PublicUserProfileCardProps = {
  hideButtons?: boolean
  username?: string
  userId?: number
}

const PublicUserProfileCardContent = ({ hideButtons = false, username, userId }: PublicUserProfileCardProps) => {
  const [isBioModalOpen, setIsBioModalOpen] = useState(false)
  const navigate = useNavigate()

  const { data: userByUsername } = useUserByUsername(username || '')
  const { data: userById } = useUserById(userId || -1)

  const user = userByUsername || userById

  const { data: reviewData } = useUserReviews(user?.id || -1)
  const { data: publicUserLicenses } = usePublicLicenses(user?.id || -1)
  const reviewsCount = reviewData?.totalReviews ?? 0
  const averageRating = reviewData?.averageRating ?? 0

  const isLoanOfficer = user?.profession === 'mortgage_loan_officer'

  const onSendInquiry = () => {
    if (!user?.id) return
    navigate(paths.marketplace.instantMatch.userId.getHref(user.id.toString()))
  }

  if (!user) {
    return null
  }

  return (
    <>
      <div
        className="flex min-w-[350px] max-w-[350px] flex-col gap-4 rounded-[40px] border-[0.5px] bg-white text-primary lg:min-w-[500px] lg:max-w-[500px]"
        style={{ boxShadow: '0px 2px 12px 1px rgba(27, 48, 41, 0.10)', borderColor: 'rgba(170, 171, 173, 0.40)' }}
      >
        <img
          src={user.coverPhoto || CoverPhotoFallback}
          alt="cover photo"
          className="h-[160px] w-full rounded-t-[40px] object-cover"
        />
        <div className="mb-[-50px] flex translate-y-[-50px] flex-col gap-2 rounded-[40px] bg-white">
          <div className="mb-[-20px] flex translate-y-[-50px] flex-col gap-2">
            <div className="flex flex-col items-center gap-2 px-5 lg:px-8">
              <img
                src={user.profilePhoto || ProfilePhotoFallback}
                alt="profile picture"
                className="mb-1 size-[100px] rounded-full border-[5px] border-white bg-white object-cover lg:mb-3"
                style={{ boxShadow: '0px 2px 12px 1px rgba(27, 48, 41, 0.10)' }}
              />
              <div className="flex flex-col items-center">
                <div className="flex items-center gap-2">
                  <h1 className={'text-[24px] font-bold lg:text-[28px]'}>{user.fullName}</h1>
                  <VerificationBadge isVerified={user.verified || false} className="mt-0.5" size={24} />
                </div>
                <div className="flex items-center gap-2">
                  <p className="text-sm text-primary lg:text-lg">{professionMap[user.profession].label}</p>
                </div>
              </div>
              {!user.biography ? (
                <div className="relative w-full text-center text-sm italic text-muted lg:text-base">
                  <p className="line-clamp-1">{user.fullName} doesn&apos;t have a bio yet.</p>
                </div>
              ) : (
                <div className="relative w-full text-sm lg:text-base">
                  <p className="line-clamp-2 text-muted">{user.biography}</p>
                  <button onClick={() => setIsBioModalOpen(true)} className="-mt-2 text-primary hover:underline">
                    Read full bio
                  </button>
                </div>
              )}
              <OtherInfoContainer
                isCurrentUser={false}
                reviewsCount={reviewsCount}
                averageRating={averageRating}
                shortAddress={user.shortAddress || ''}
              />
              {!hideButtons && ['mortgage_loan_officer', 'real_estate_agent'].includes(user.profession) && (
                <div className="mt-2 flex flex-col items-center gap-3 lg:flex-row">
                  <Button
                    onClick={onSendInquiry}
                    variant="outline"
                    size="default"
                    className="rounded-full border-[#d8d8d6] bg-[#f8f7f5] px-6"
                  >
                    Send inquiry
                  </Button>
                  <RequestCallButton username={username as string} />
                </div>
              )}
              <div className="my-4 h-px w-full bg-[#efefef] lg:my-7" />
            </div>
            <div className="flex flex-col gap-4 px-5 lg:gap-6 lg:px-8">
              <GeneralInfoSection
                icon={<VerifiedLicensesIcon className="size-7 text-primary lg:size-8" />}
                title="Verified Licenses"
                subtitle={isLoanOfficer ? publicUserLicenses?.[0].licenseNumber || undefined : undefined}
                text={publicUserLicenses
                  ?.map((license) =>
                    isLoanOfficer
                      ? `${statesMap[license.state as unknown as State]?.label}`
                      : `${statesMap[license.state as unknown as State]?.label} (#${license.licenseNumber})`,
                  )
                  .join(` • `)}
              />
              {user?.companyName && (
                <GeneralInfoSection
                  icon={<WorksAtIcon className="size-7 text-primary lg:size-8" />}
                  title="Works at"
                  text={user?.companyName || 'N/A'}
                />
              )}
              {user?.expertise && user.expertise?.length > 0 && (
                <GeneralInfoSection
                  icon={<ExpertiseIcon className="size-7 text-primary lg:size-8" />}
                  title="Expertise"
                  text={user?.expertise
                    ?.map(
                      (e) => expertiseMap[user?.profession as unknown as Profession][e as unknown as Expertise]?.label,
                    )
                    .join(` • `)}
                />
              )}
              <GeneralInfoSection
                icon={<LanguagesIcon className="size-7 text-primary lg:size-8" />}
                title="Languages"
                text={user?.languages?.map((e) => languageMap[e as unknown as Language]?.label).join(` • `)}
              />
            </div>
          </div>
        </div>
      </div>

      <Dialog open={isBioModalOpen} onOpenChange={setIsBioModalOpen}>
        <DialogContent className="w-[500px] max-w-[90vw] p-8 sm:rounded-[40px]">
          <DialogHeader>
            <DialogTitle className="mb-4 text-[26px] font-bold">About {user.fullName}</DialogTitle>
          </DialogHeader>
          <p className="whitespace-pre-wrap text-muted">{user.biography}</p>
        </DialogContent>
      </Dialog>
    </>
  )
}

const PublicUserProfileCard = ({ hideButtons = false, username, userId }: PublicUserProfileCardProps) => {
  return (
    <Suspense fallback={<PublicProfileUserCardSkeleton />}>
      <PublicUserProfileCardContent hideButtons={hideButtons} username={username} userId={userId} />
    </Suspense>
  )
}

export default PublicUserProfileCard
